/* ============================================================
   Technology Stack — equal label column width
   ------------------------------------------------------------
   Every row in the Technology Stack table starts with a
   `.tech-stack-label` (icon + "Backend", "Cloud & DevOps",
   "Database"…). Their natural widths differ, so the chip lists
   next to them never line up.

   This measures the natural (max-content) width of every
   `.tech-stack-label` inside a `.tech-stack-table`, takes the
   widest one and applies it as `max-width` to all labels of that
   table — each table is measured on its own, so two stack sections
   on one page never influence each other. Combined with the
   `width: 100%` already in tech-stack.css, every label ends up
   exactly as wide as the widest one.

   The value is also exposed on the table as
   `--tech-stack-label-max-width` for CSS to reuse if needed. The
   hard-coded `max-width: 25rem` in tech-stack.css stays as the
   no-JS fallback — the inline value set here overrides it.

   Re-runs on resize and once webfonts are ready, since both change
   how wide the text renders.
   ============================================================ */

const TABLE_SELECTOR = ".tech-stack-table";
const LABEL_SELECTOR = ".tech-stack-label";
const RESIZE_DEBOUNCE = 150;

// Natural width of a label, measured free of the flex layout it
// sits in: `max-content` + no shrinking + no cap means the value we
// read back is the label's own content width (icon + gap + text +
// padding), not the width the row gave it.
function measureNaturalWidth(label) {
  const { maxWidth, width, flexShrink } = label.style;

  label.style.maxWidth = "none";
  label.style.width = "max-content";
  label.style.flexShrink = "0";

  const naturalWidth = label.getBoundingClientRect().width;

  label.style.maxWidth = maxWidth;
  label.style.width = width;
  label.style.flexShrink = flexShrink;

  return naturalWidth;
}

function applyMaxWidth(table) {
  const labels = table.querySelectorAll(LABEL_SELECTOR);
  if (!labels.length) return;

  // Clear the previous pass first — otherwise an earlier (wider)
  // value would be re-measured and the width could only ever grow.
  labels.forEach((label) => {
    label.style.maxWidth = "";
  });

  let maxWidth = 0;

  labels.forEach((label) => {
    maxWidth = Math.max(maxWidth, measureNaturalWidth(label));
  });

  // Sub-pixel widths get truncated by the browser and would wrap the
  // longest label, so round up.
  const value = `${Math.ceil(maxWidth)}px`;

  table.style.setProperty("--tech-stack-label-max-width", value);

  labels.forEach((label) => {
    label.style.maxWidth = value;
  });
}

export function initTechStackLabels() {
  const tables = document.querySelectorAll(TABLE_SELECTOR);
  if (!tables.length) return;

  const update = () => tables.forEach(applyMaxWidth);

  update();

  // Fallback fonts are usually narrower than the real ones, so a
  // width measured before the webfonts land would be too small.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(update);
  }

  let resizeTimer = null;

  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(update, RESIZE_DEBOUNCE);
  });
}
